"use client";
import { useCallback, useEffect, useMemo, useState } from "react";
import styles from "./index.module.scss";
import {
  MdKeyboardArrowLeft,
  MdKeyboardArrowRight,
  MdKeyboardDoubleArrowLeft,
} from "react-icons/md";
import classNames from "classnames";
import type { Table } from "@tanstack/react-table";
import type { Transaction } from "@/types/bridge-scanner.types";
import type { UseInfiniteQueryResult } from "@tanstack/react-query";
import LoadingIcon from "@components/layout/loading-icon";

export const PAGE_LENGTH = 10;

type Props = {
  table: Table<Transaction>;
  queryControl: UseInfiniteQueryResult<Transaction[] | null, Error>;
};

const TablePaginator = ({ table, queryControl }: Props) => {
  const [paginationButtons, setPaginationButtons] = useState<number[]>([]);

  // Keep table out of useEffect to avoid infinite loop
  const pageIndex = table.getState().pagination.pageIndex;
  const pageCount = table.getPageCount();
  const hasNextPage = queryControl.hasNextPage;

  const fetchNextPage = useMemo(
    () => queryControl.fetchNextPage,
    [queryControl.fetchNextPage],
  );

  useEffect(() => {
    (async () => {
      let buttonNumbers: number[] = [];

      // If the active page is less than or equal to 3, render 1-5 on the buttons
      if (pageIndex <= 2) {
        buttonNumbers = Array.from(
          { length: pageCount > 5 ? 5 : pageCount },
          (_, i) => i,
        );
        return setPaginationButtons(buttonNumbers);
      }

      // Render out the pagination numbers to the length of the page count if less than 5
      if (pageCount <= 5) {
        buttonNumbers = Array.from({ length: pageCount }, (_, i) => i);
        return setPaginationButtons(buttonNumbers);
      }

      // If the active page is 2 away from the last page, fetch more data and shift the page count
      if (pageIndex >= pageCount - 2) {
        // Before fetching,
        buttonNumbers = Array.from({ length: 5 }, (_, i) =>
          Math.max(0, Math.min(pageCount - 1, pageCount - 5 + i)),
        );
        setPaginationButtons(buttonNumbers);
        if (hasNextPage) {
          await fetchNextPage?.();
          return setPaginationButtons(buttonNumbers);
        }
        return;
      }

      // If none of the above is true we shift the count accordingly
      setPaginationButtons([
        pageIndex - 2,
        pageIndex - 1,
        pageIndex,
        pageIndex + 1,
        pageIndex + 2,
      ]);
    })();
  }, [fetchNextPage, pageCount, pageIndex, hasNextPage]);

  const goToNextPage = async () => {
    if (table.getCanNextPage()) table.nextPage();
  };

  const goToPrevPage = () => {
    if (table.getCanPreviousPage()) {
      table.previousPage();
    } else return;
  };

  return (
    <div className={styles.paginatorContainer}>
      <div className={styles.group}>
        <button className={styles.pagination} onClick={table.firstPage}>
          <MdKeyboardDoubleArrowLeft size={20} />
        </button>
        <button className={styles.pagination} onClick={goToPrevPage}>
          <MdKeyboardArrowLeft size={20} />
        </button>
      </div>
      <div className={styles.group}>
        {paginationButtons.length ? (
          paginationButtons.map((p) => (
            <button
              onClick={() => table.setPageIndex(p)}
              key={p}
              className={classNames(
                styles.pagination,
                p === table.getState().pagination.pageIndex && styles.active,
              )}
            >
              {p + 1}
            </button>
          ))
        ) : (
          <button className={classNames(styles.pagination)}>
            <LoadingIcon size={15} />
          </button>
        )}
      </div>
      <div className={styles.group}>
        <button
          className={styles.pagination}
          onClick={goToNextPage}
          disabled={queryControl.isFetchingNextPage}
        >
          {queryControl.isFetchingNextPage ? (
            <LoadingIcon size={15} />
          ) : (
            <MdKeyboardArrowRight size={20} />
          )}
        </button>
      </div>
    </div>
  );
};

export default TablePaginator;
